import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CaiGenerationEntitlementContext } from "../opportunities/CaiGenerationEntitlementContext";
import { getCaiAssetOpportunities } from "../../lib/caiOpportunityRunApi";
import { supabase } from "../../lib/supabase";
import CaiDurableJobPanel from "../opportunities/CaiDurableJobPanel";
import { OpportunityWorkspaceHeader, OpportunityEmptyState, OpportunityDetailHeader } from "../opportunities/OpportunityPresentation";
import "../opportunities/OpportunityWorkspace.css";

type Category = "asset" | "entity";

type WorkspaceProps = {
  accountId: string;
  runId?: string | null;
  accountName?: string | null;
  accountType?: string | null;
  isPlatformAdmin?: boolean;
  category: Category;
  briefLayout?: "persistent" | "focus";
};

type Opportunity = {
  accountId: string;
  accountType?: string | null;
  runId?: string | null;
  recommendationId?: string | null;
  recommendationSourceKey: string;
  recommendationTitle: string;
  recommendationType: string;
  targetEntity?: string | null;
  recommendedOutput?: string | null;
  targetAsset: string;
  targetAudience: string;
  evidencePattern: string;
  expectedResult: string;
  nextStep?: string | null;
  whyItMatters?: string | null;
  requiresMappingReview?: boolean;
  productArea: "local" | "competitive";
  opportunityCategory: Category;
};

function mapEntityOpportunity(row: Record<string, any>, accountId: string, runId: string | null, accountName: string, accountType?: string | null): Opportunity {
  const key = String(row.id || row.recommendation_type || row.title || "entity-opportunity");
  const title = String(row.title || row.recommendation_type || "Entity foundation opportunity");
  const action = String(row.recommended_action || "Strengthen the reviewed entity foundation.");
  return {
    accountId,
    accountType,
    runId,
    recommendationId: row.id || key,
    recommendationSourceKey: key,
    recommendationTitle: title,
    recommendationType: String(row.recommendation_type || "entity_foundation"),
    targetEntity: row.entity_name || accountName,
    recommendedOutput: "Entity foundation brief",
    targetAsset: "Entity foundation",
    targetAudience: accountName,
    evidencePattern: String(row.summary || row.why_it_matters || action),
    expectedResult: action,
    nextStep: action,
    whyItMatters: row.why_it_matters || null,
    requiresMappingReview: row.activation_type === "review_entity_mapping",
    productArea: accountType === "competitive" ? "competitive" : "local",
    opportunityCategory: "entity",
  };
}

export default function CaiCategoryWorkspace({ accountId, runId = null, accountName = "this account", accountType, isPlatformAdmin = false, category, briefLayout = "persistent" }: WorkspaceProps) {
  const generationEnabled = useContext(CaiGenerationEntitlementContext);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [assetReviewStatus, setAssetReviewStatus] = useState("no_reviewed_assets");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [focused, setFocused] = useState(false);
  const [readingFocus, setReadingFocus] = useState(false);
  const [savedBrief, setSavedBrief] = useState(false);
  const [knownStatuses, setKnownStatuses] = useState<Record<string, string>>({});
  const [mobileListOpen, setMobileListOpen] = useState(true);
  const onStatusChange = useCallback((status: string) => {
    if (selectedKey) setKnownStatuses(previous => previous[selectedKey] === status ? previous : { ...previous, [selectedKey]: status });
  }, [selectedKey]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setMessage("");
    setAssetReviewStatus("no_reviewed_assets");
    setOpportunities([]);
    setSelectedKey(null);
    setSearch("");
    setFocused(false);
    setReadingFocus(false);
    setSavedBrief(false);
    setKnownStatuses({});
    setMobileListOpen(true);

    async function load() {
      if (!accountId || (category !== "asset" && !runId)) {
        if (active) {
          setLoading(false);
          setMessage("No completed analysis context is available for this account. Entity recommendations are shown when the selected run has open work.");
        }
        return;
      }

      const result: any = category === "asset"
        ? await getCaiAssetOpportunities(accountId, runId)
        : await supabase.from("entity_builder_recommendations_open_v1").select("id,recommendation_type,entity_area,entity_name,title,summary,why_it_matters,recommended_action,activation_type,priority,confidence_score").eq("account_id", accountId).or(`run_id.eq.${runId},run_id.is.null`).limit(20);

      if (!active) return;
      setLoading(false);
      if (category === "asset" && result.status === "error") {
        setMessage(result.code === "cai_account_access_denied" ? "You do not have access to this account's asset workspace." : "This workspace is temporarily unavailable.");
        return;
      }
      if (result.error) {
        setMessage("This workspace is temporarily unavailable.");
        return;
      }
      if (category === "asset") setAssetReviewStatus(result.reviewStatus || "no_reviewed_assets");

      const resolvedAssetRunId = category === "asset" ? result.runId || runId || null : runId;
      const next = category === "asset"
        ? (result.assets || []).map((item: any) => ({
          accountId,
          accountType,
          runId: resolvedAssetRunId,
          recommendationId: item.sourceAssetId || item.id,
          recommendationSourceKey: item.key,
          recommendationTitle: item.title,
          recommendationType: item.recommendationType || "existing_page_optimization",
          targetEntity: accountName,
          recommendedOutput: item.outputType,
          targetAsset: item.targetAsset,
          targetAudience: item.audience,
          evidencePattern: item.evidencePattern,
          expectedResult: item.expectedResult,
          productArea: accountType === "competitive" ? "competitive" : "local",
          opportunityCategory: "asset" as const,
        }))
        : (result.data || []).map((row) => mapEntityOpportunity(row, accountId, runId, accountName, accountType));

      setOpportunities(next);
      setSelectedKey(next[0]?.recommendationSourceKey || null);
      if (!next.length) setMessage(category === "asset"
        ? result.reviewStatus === "evidence_correction_required"
          ? "A reviewed asset needs evidence correction before it can be used for a brief."
          : result.reviewStatus === "evidence_review_required"
            ? "Reviewed asset evidence needs attention before an optimization brief is available."
            : "No reviewed asset opportunities are ready for this run."
        : "No open entity recommendations are available for this run.");
    }

    void load();
    return () => { active = false; };
  }, [accountId, accountName, accountType, category, runId]);

  const selected = useMemo(
    () => opportunities.find((item) => item.recommendationSourceKey === selectedKey) || opportunities[0] || null,
    [opportunities, selectedKey],
  );

  const header = <OpportunityWorkspaceHeader title={category === "asset" ? "Assets" : "Entity"} description={category === "asset" ? "Review operator-confirmed page evidence and read saved briefs. Discovery candidates stay in operator review; new generation is available only when the account is enabled." : "Review business identity, entity clarity and the next governed step."} />;
  if (loading) return <section data-testid={`oe-workspace-${category}-content`}>{header}<OpportunityEmptyState title="Loading opportunities">Loading {category} opportunities…</OpportunityEmptyState></section>;
  if (!selected) return <section data-testid={`oe-workspace-${category}-content`}>{header}<OpportunityEmptyState title={message.includes("unavailable") || message.includes("access") ? "Workspace unavailable" : category === "asset" ? "No eligible pages available" : "No entity work available"} error={message.includes("unavailable") || message.includes("access")}>{message}</OpportunityEmptyState>{category === "asset" && message.startsWith("No reviewed") && <p className="oe-governed-next-step">This view includes pages accepted by the existing asset eligibility rules, not unreviewed discovery candidates. An empty response does not establish whether other inventory or asset signals exist. No brief generation is available from this empty state.</p>}</section>;

  const visible = opportunities.filter(item => item.recommendationTitle.toLocaleLowerCase().includes(search.toLocaleLowerCase()));
  const focusLayout = readingFocus;
  return <section data-testid={`oe-workspace-${category}-content`}>{!focusLayout && header}{!focusLayout && category === "asset" && assetReviewStatus === "evidence_correction_required" && <p className="oe-governed-next-step" role="status">Some reviewed asset evidence needs correction before those pages can be used for briefs.</p>}<div className={`oe-action-layout${focusLayout ? " is-focused" : ""}${focused ? " brief-open" : ""}`}>
    {!focusLayout && <button type="button" className="oe-mobile-list-toggle cai-brief-back" aria-expanded={mobileListOpen} onClick={() => setMobileListOpen(value => !value)}>{mobileListOpen ? "Show selected opportunity" : "Choose opportunity"}</button>}
    <nav className={`oe-navigator${mobileListOpen ? "" : " mobile-collapsed"}`} aria-label={`${category} opportunities`} hidden={focusLayout}>
      <strong>Opportunities</strong>
      <label><span style={{ display: "block", marginTop: 12, fontSize: 12 }}>Find an opportunity</span><input type="search" value={search} onChange={event => setSearch(event.target.value)} /></label>
      {visible.map(item => <button className="oe-opportunity" key={item.recommendationSourceKey} type="button" aria-current={item.recommendationSourceKey === selected.recommendationSourceKey ? "true" : undefined} title={item.recommendationTitle} onClick={() => { if (item.recommendationSourceKey !== selectedKey) { setSelectedKey(item.recommendationSourceKey); setSavedBrief(false); } setMobileListOpen(false); }}>
        <strong>{item.recommendationTitle.replace(/^Improve existing asset:\s*/i, "")}</strong>
        <small>{knownStatuses[item.recommendationSourceKey] === "completed" ? "Brief ready" : category === "asset" ? "Reviewed asset opportunity" : "Entity opportunity"}</small>
      </button>)}
      {!visible.length && <p>No opportunities match this search.</p>}
    </nav>
    <div>
      <div hidden={savedBrief}>
        <OpportunityDetailHeader title={selected.recommendationTitle} target={category === "entity" ? selected.targetEntity : selected.targetAsset} action={selected.nextStep || selected.expectedResult}>
        {category === "entity" && selected.whyItMatters && <section><h4>Why it matters</h4><p>{selected.whyItMatters}</p></section>}
        {selected.evidencePattern && <details open><summary>{category === "entity" ? "Supporting evidence" : "Why this matters · Evidence context"}</summary><p>{selected.evidencePattern}</p></details>}
        </OpportunityDetailHeader>
      </div>
      {category === "asset" ? <CaiDurableJobPanel key={`${selected.accountId}:${selected.runId}:${selected.recommendationSourceKey}`} accountName={accountName} enabled generationEnabled={generationEnabled} isPlatformAdmin={isPlatformAdmin} opportunity={selected} workspacePresentation briefLayout={briefLayout} onFocusChange={setFocused} onReadingFocusChange={setReadingFocus} onSavedBriefChange={setSavedBrief} onStatusChange={onStatusChange} /> : <section className="oe-governed-next-step"><h4>{selected.requiresMappingReview ? "Authorized entity review required" : "Informational opportunity"}</h4><p>{selected.requiresMappingReview ? "Use the existing authorized entity-mapping review workflow to confirm, map or reject this entity. This workspace does not grant approval access." : "Evidence is available for review; no customer action is connected to this opportunity here."} Selecting this opportunity does not approve a mapping or change the account.</p></section>}
    </div>
    </div>
  </section>;
}
